import moment from 'moment-timezone'
import {useMemo} from 'react'

/**
 * Returns the current time in the specified timezone, rounded to the specified time unit.
 *
 * @param timezone - The timezone identifier (e.g., 'America/New_York', 'Europe/London')
 * @param roundTo - The time unit to round to. Defaults to 'minute'. Supported values: 'minute', 'hour', 'day', 'month'
 * @returns A moment object representing the current time in the specified timezone, rounded down to the specified unit
 *
 * @example
 * ```tsx
 * // Get current time rounded to the nearest minute
 * const currentTime = useCurrentTime('America/New_York', 'minute')
 *
 * // Get current time rounded to the nearest hour
 * const currentHour = useCurrentTime('Europe/London', 'hour')
 * ```
 */
export const useCurrentTime = (timezone: string, roundTo: moment.unitOfTime.StartOf = 'minute') => {
  // This is a value that changes only when the rounded time unit changes
  const roundingKey = Math.floor(Date.now() / getIntervalMs(roundTo))

  // Create a time key that changes only when the rounded time unit changes
  const timeKey = useMemo(() => {
    return moment().tz(timezone).startOf(roundTo).valueOf()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezone, roundTo, roundingKey])

  const currentTime = useMemo(() => {
    return moment().tz(timezone).startOf(roundTo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezone, roundTo, timeKey])

  return currentTime
}

// Helper function to get the interval in milliseconds for each unit
const getIntervalMs = (unit: moment.unitOfTime.StartOf): number => {
  switch (unit) {
    case 'minute':
      return 60 * 1000 // 1 minute
    case 'hour':
      return 60 * 60 * 1000 // 1 hour
    case 'day':
      return 24 * 60 * 60 * 1000 // 1 day
    case 'month':
      return 30 * 24 * 60 * 60 * 1000 // ~1 month (approximation)
    default:
      return 60 * 1000 // default to 1 minute
  }
}
